import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Network, ChevronLeft, ChevronRight } from 'lucide-react';
import Slider from 'react-slick';
import type { CognitiveBias } from '../../types';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface RelatedBiasesProps {
  biases: CognitiveBias[];
}

export const RelatedBiases: React.FC<RelatedBiasesProps> = ({ biases }) => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);

  if (!biases || biases.length === 0) return null;

  const sliderRef = React.useRef<Slider>(null);
  const slidesToShow = Math.min(2, biases.length);
  
  // Determine if pagination is needed - only needed if we have more biases than can be shown
  const needsPagination = biases.length > slidesToShow;

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const goToSlide = (index: number) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  // Function to determine if a dot is active
  const isDotActive = (dotIndex: number): boolean => {
    return dotIndex === activeSlide;
  };

  const settings = {
    dots: false,
    infinite: needsPagination,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1, // Always scroll one item at a time for smoother transitions
    arrows: false,
    beforeChange: (_current: number, next: number) => {
      setActiveSlide(next);
    },
    responsive: [
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: biases.length > 1 // Enable infinite scrolling on mobile if more than 1 bias
        }
      }
    ]
  };

  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center justify-center text-center gap-3">
        <div className="bg-orange-100 p-2 rounded-lg">
          <Network className="w-5 h-5 text-orange-600" />
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 text-center leading-tight hyphens-auto">Related Biases</h2>
      </div>
      
      <div className="related-biases-slider-container">
        <div className="related-biases-slider w-full overflow-visible">
          {biases.length <= 2 ? (
            // For 2 or fewer biases on desktop, render them directly without the slider
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {biases.map(bias => {
                if (!bias) return null;
                
                return (
                  <div key={bias.id} className="px-2 pb-4">
                    <Link
                      to={`/bias/${bias.id}/`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setTimeout(() => {
                          navigate(`/bias/${bias.id}/`);
                        }, 500);
                      }}
                      className="group bg-white/80 backdrop-blur-sm p-4 sm:p-5 rounded-2xl border border-gray-100 
                        shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.1)]
                        hover:shadow-[0_4px_6px_rgba(0,0,0,0.05),0_5px_15px_rgba(0,0,0,0.1)]
                        hover:border-orange-200 transition-all duration-300 flex flex-col h-full overflow-hidden block w-full"
                    >
                      <div className="flex-grow w-full">
                        <h3 className="text-lg font-semibold mb-3 break-words w-full">{bias.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-4 break-words w-full">
                          {bias.short_description}
                        </p>
                      </div>
                      <div className="text-orange-500 text-sm font-medium flex items-center gap-2">
                        <span>Learn More</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : (
            // For more than 2 biases on desktop, use the slider
            <Slider ref={sliderRef} {...settings}>
              {biases.map(bias => {
                if (!bias) return null;
                
                return (
                  <div key={bias.id} className="px-2 pb-4">
                    <Link
                      to={`/bias/${bias.id}/`}
                      onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setTimeout(() => {
                          navigate(`/bias/${bias.id}/`);
                        }, 500);
                      }}
                      className="group bg-white/80 backdrop-blur-sm p-4 sm:p-5 rounded-2xl border border-gray-100 
                        shadow-[0_1px_3px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.1)]
                        hover:shadow-[0_4px_6px_rgba(0,0,0,0.05),0_5px_15px_rgba(0,0,0,0.1)]
                        hover:border-orange-200 transition-all duration-300 flex flex-col h-full overflow-hidden block w-full"
                    >
                      <div className="flex-grow w-full">
                        <h3 className="text-lg font-semibold mb-3 break-words w-full">{bias.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-4 break-words w-full">
                          {bias.short_description}
                        </p>
                      </div>
                      <div className="text-orange-500 text-sm font-medium flex items-center gap-2">
                        <span>Learn More</span>
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                      </div>
                    </Link>
                  </div>
                );
              })}
            </Slider>
          )}
        </div>
      </div>
      
      {/* Only show pagination controls if we need them */}
      {biases.length > slidesToShow && (
        <div className="flex items-center justify-center gap-4 pt-0 pb-2">
          <button
            onClick={goToPrev}
            className="p-2 rounded-xl hover:bg-orange-50 transition-colors focus:outline-none"
            aria-label="Previous bias"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
          {Array.from({ length: biases.length }).map((_, i) => (
            <button 
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${isDotActive(i) ? 'bg-orange-500' : 'bg-gray-300'}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to bias ${i + 1}`}
            />
          ))}
          <button
            onClick={goToNext}
            className="p-2 rounded-xl hover:bg-orange-50 transition-colors focus:outline-none"
            aria-label="Next bias"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
        </div>
      )}

      <style>
        {`
          .related-biases-slider-container {
            overflow: hidden;
            width: 100%;
          }
          .related-biases-slider {
            padding: 8px 0;
            width: 100%;
            overflow: hidden;
          }
          .slick-list {
            overflow: hidden !important;
          }
          .slick-track {
            display: flex !important;
          }
          .slick-slide {
            height: inherit !important;
          }
          .slick-slide > div {
            height: 100%;
            padding: 0 8px;
          }
        `}
      </style>
    </div>
  );
}; 